import { useCallback, useEffect, useRef } from "react";
import Cookies from "js-cookie";
import { useAppSelector, useAppDispatch } from "hooks/redux";

import {
    setLogs,
    appendLogs,
    setHasMore,
    setLastPage,
    setUnread,
    setLoading,
    NotificationSource,
    loadNotifications,
    prependLogs,
} from "store/reducers/NotificationSlice";

export default function NotificationSockets() {
    const dispatch = useAppDispatch();
    const { filters } = useAppSelector((state) => state.notifications);

    const changesRef = useRef<WebSocket | null>(null);
    const systemRef = useRef<WebSocket | null>(null);
    const webAlertsRef = useRef<WebSocket | null>(null);

    const selectedRobotId = useAppSelector(
        (state) => state.robots.selectedRobotId,
    );

    // ==================== ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ ====================

    const buildSocketUrl = (): string => {
        const token = Cookies.get("access");
        if (!token) return "";

        return `${process.env.REACT_APP_SOCKET_SECURE}${process.env.REACT_APP_BASE_API_DOMAIN}/wss/group_action_logs/?token=${encodeURIComponent(token)}`;
    };

    const createSocket = (source: NotificationSource, initFilters: any) => {
        const token = Cookies.get("access");
        if (!token) {
            console.warn(`Нет токена для ${source}`);
            return null;
        }

        const url = buildSocketUrl();
        if (!url) return null;

        const socket = new WebSocket(url);

        socket.onopen = () => {
            dispatch(setLoading({ source, loading: true }));
            sendFetchPage(socket, source, 1);
        };

        socket.onmessage = (event) => {
            try {
                const data = JSON.parse(event.data);

                //  Реал-тайм одиночное уведомление

                if (data.id && !Array.isArray(data.logs) && data.source) {
                    const source = data.source as NotificationSource;
                    if (!source) return;
                    if (data.last_page !== undefined) {
                        console.log(data.last_page)
                        dispatch(
                            setLastPage({
                                source,
                                page: Number(data.last_page),
                            }),
                        );
                    }
                    let newLog: any;

                    if (source === "web_alerts") {
                        newLog = {
                            source: "web_alerts" as const,
                            id: data.id,
                            created_at: data.created_at,
                            level: data.level,
                            text: data.text,
                            is_read: false,
                        };
                    } else if (source === "changes") {
                        newLog = {
                            source: "changes" as const,
                            id: data.id,
                            action_by_id: data.action_by_id,
                            action_by: data.action_by,
                            group_name: data.group_name,
                            is_read: false,
                            action: data.action,
                            created_at: data.created_at,
                            ticker: data.ticker,
                            meta: {
                                before: data.meta?.before ?? null,
                                after: data.meta?.after ?? null,
                            },
                        };
                    } else if (source === "system") {
                        newLog = {
                            source: "system" as const,
                            id: data.id,
                            created_at: data.created_at,
                            level: data.level,
                            module: data.module,
                            text: data.text,
                            ticker_name: data.ticker_name,
                            tab_id: data.tab_id,
                        };
                    }

                    if (newLog) {
                        dispatch(prependLogs({ source, logs: [newLog] }));

                        if (data.unread !== undefined) {
                            const unreadCount = Number(data.unread);

                            if (source === "web_alerts") {
                                dispatch(
                                    setUnread({ webAlertsUnread: unreadCount }),
                                );
                            } else if (source === "changes") {
                                dispatch(
                                    setUnread({ changesUnread: unreadCount }),
                                );
                            } else if (source === "system") {
                                dispatch(
                                    setUnread({ systemUnread: unreadCount }),
                                );
                            }
                        }
                    }
                }
                if (Array.isArray(data.logs)) {
                    const newLogs = data.logs.map((raw: any) => {
                        if (source === "changes") {
                            return {
                                source: "changes" as const,
                                id: raw.id,
                                action_by_id: raw.action_by_id,
                                action_by: raw.action_by,
                                group_name: raw.group_name,
                                is_read: raw.is_read ?? false,
                                action: raw.action,
                                created_at: raw.created_at,
                                ticker: raw.ticker,
                                meta: {
                                    before: raw.meta?.before ?? null,
                                    after: raw.meta?.after ?? null,
                                },
                            };
                        }
                        if (source === "system") {
                            return {
                                source: "system" as const,
                                id: raw.id,
                                created_at: raw.created_at,
                                level: raw.level,
                                module: raw.module,
                                text: raw.text,
                                ticker_name: raw.ticker_name,
                                tab_id: raw.tab_id,
                            };
                        }
                        return {
                            source: "web_alerts" as const,
                            id: raw.id,
                            created_at: raw.created_at,
                            level: raw.level,
                            text: raw.text,
                            is_read: raw.is_read ?? false,
                        };
                    });

                    // Определяем — это первая страница или подгрузка
                    const isFirstPage = data.page === 1 || !data.page;

                    if (isFirstPage) {
                        dispatch(setLogs({ source, logs: newLogs }));
                    } else {
                        dispatch(appendLogs({ source, logs: newLogs }));
                    }

                    dispatch(
                        setHasMore({ source, hasMore: newLogs.length > 0 }),
                    );

                    if (data.last_page) {
                        dispatch(setLastPage({ source, page: data.last_page }));
                    }

                    // Обработка unread из ответа с логами
                    if (data.unread !== undefined) {
                        const unreadCount = Number(data.unread);

                        if (!isNaN(unreadCount)) {
                            if (source === "web_alerts") {
                                dispatch(
                                    setUnread({ webAlertsUnread: unreadCount }),
                                );
                            } else if (source === "changes") {
                                dispatch(
                                    setUnread({ changesUnread: unreadCount }),
                                );
                            } else if (source === "system") {
                                dispatch(
                                    setUnread({ systemUnread: unreadCount }),
                                );
                            }
                        }
                    }
                }
                if (data.total_unread !== undefined) {
                    dispatch(
                        setUnread({ totalUnread: Number(data.total_unread) }),
                    );
                }
                // Обработка общих unread счётчиков
                if (
                    data.total_unread !== undefined ||
                    data.changes_unread !== undefined ||
                    data.system_unread !== undefined ||
                    data.web_alerts_unread !== undefined
                ) {
                    dispatch(
                        setUnread({
                            totalUnread: data.total_unread,
                            changesUnread: data.changes_unread,
                            systemUnread: data.system_unread,
                            webAlertsUnread: data.web_alerts_unread,
                        }),
                    );
                }
            } catch (err) {
                console.error(`[${source}] Parse error:`, err);
            } finally {
                dispatch(setLoading({ source, loading: false }));
            }
        };

        socket.onerror = (err) => {
            console.error(`[${source}] Socket error:`, err);
            dispatch(setLoading({ source, loading: false }));
        };

        socket.onclose = () => {
            console.log(`[${source}] Socket closed`);
        };

        return socket;
    };
    const sockets = useRef<Record<NotificationSource, WebSocket | null>>({
        changes: null,
        system: null,
        web_alerts: null,
    });
    const sendFetchPage = (
        socket: WebSocket | null,
        source: NotificationSource,
        page: number,
        customFilters?: any,
    ) => {
        if (!socket || socket.readyState !== WebSocket.OPEN) return;
        dispatch(setLoading({ source, loading: true }));
        const payload: any = {
            source, // ← это обязательно!
            page,
            ...(customFilters || filters[source]),
        };

        // Убираем пустые/null значения
        Object.keys(payload).forEach((key) => {
            if (
                payload[key] === "" ||
                payload[key] === null ||
                payload[key] === undefined
            ) {
                delete payload[key];
            }
        });

        socket.send(JSON.stringify(payload));
    };

    useEffect(() => {
        if (!selectedRobotId) return;

        changesRef.current?.close();
        systemRef.current?.close();
        webAlertsRef.current?.close();

        const timeout1 = setTimeout(() => {
            changesRef.current = createSocket("changes", filters.changes);
        }, 0);

        const timeout2 = setTimeout(() => {
            systemRef.current = createSocket("system", filters.system);
        }, 50);

        const timeout3 = setTimeout(() => {
            webAlertsRef.current = createSocket(
                "web_alerts",
                filters.web_alerts,
            );
        }, 100);

        return () => {
            clearTimeout(timeout1);
            clearTimeout(timeout2);
            clearTimeout(timeout3);
            changesRef.current?.close();
            systemRef.current?.close();
            webAlertsRef.current?.close();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedRobotId]);

    // Реакция на изменение фильтров (перезагрузка первой страницы)
    useEffect(() => {
        sendFetchPage(changesRef.current, "changes", 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.changes, selectedRobotId]);

    useEffect(() => {
        sendFetchPage(systemRef.current, "system", 1);
    }, [filters.system, selectedRobotId]);

    useEffect(() => {
        sendFetchPage(webAlertsRef.current, "web_alerts", 1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters.web_alerts, selectedRobotId]);

    useEffect(() => {
        const safeSend = (socket: WebSocket | null, payload: any) => {
            if (!socket || socket.readyState !== WebSocket.OPEN) {
                console.warn(
                    `Socket is not OPEN (state: ${socket?.readyState}), cannot send`,
                );
                return;
            }

            try {
                socket.send(JSON.stringify(payload));
            } catch (e) {
                console.error("Failed to send through socket:", e);
            }
        };
        (window as any).notificationSocketMethods = {
            loadMore: (
                source: NotificationSource,
                page: number,
                customFilters?: any,
            ) => {
                if (source === "changes") {
                    sendFetchPage(
                        changesRef.current,
                        "changes",
                        page,
                        customFilters,
                    );
                } else if (source === "system") {
                    sendFetchPage(
                        systemRef.current,
                        "system",
                        page,
                        customFilters,
                    );
                } else if (source === "web_alerts") {
                    sendFetchPage(
                        webAlertsRef.current,
                        "web_alerts",
                        page,
                        customFilters,
                    );
                }
            },
            markAsRead: (id: number) => {
                safeSend(changesRef.current, {
                    action: "read_notification",
                    id,
                });
            },
            markSystemRead: () => {
                safeSend(systemRef.current, {
                    action: "read_notification",
                });
            },
            markChangesRead: () => {
                safeSend(changesRef.current, {
                    action: "read_all",
                });
            },
            markWebAlertsRead: () => {
                safeSend(webAlertsRef.current, {
                    action: "read_notification",
                });
            },
        };

        return () => {
            delete (window as any).notificationSocketMethods;
        };
    }, []);

    return null;
}
